import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { useTranslation } from "react-i18next";
import { sanitizeStudyCodeInput } from "../../utils/studyCodeUtils";

export type AvailabilityState =
  | "idle"
  | "checking"
  | "available"
  | "unavailable"
  | "error";

type Props = {
  mode: "embedded" | "settings";
  studyId?: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  hideDescription?: boolean;
  onAvailabilityChange?: (state: AvailabilityState, checkedValue: string) => void;
  onSuccess?: () => void;
};

const StudyCodeRegistration = ({
  mode,
  studyId,
  value,
  onChange,
  readOnly = false,
  hideDescription = false,
  onAvailabilityChange,
  onSuccess,
}: Props) => {
  const { t } = useTranslation("studies");
  const [availability, setAvailability] = useState<AvailabilityState>("idle");
  const [checkedInput, setCheckedInput] = useState("");
  const [submitLoader, setSubmitLoader] = useState(false);
  const checkSeqRef = useRef(0);

  const checkAvailability = useMemo(
    () =>
      debounce(async (code: string) => {
        const trimmed = sanitizeStudyCodeInput(code);
        if (!trimmed) {
          setAvailability("idle");
          setCheckedInput("");
          onAvailabilityChange?.("idle", "");
          return;
        }

        const seq = ++checkSeqRef.current;
        setAvailability("checking");
        onAvailabilityChange?.("checking", "");

        try {
          const res: AxiosResponse = await http.get(
            `/study/study-code/availability`,
            { params: { code: trimmed } },
          );
          if (seq !== checkSeqRef.current) return;
          const data = res.data?.data;
          const available = data?.available === true;
          const nextState: AvailabilityState = available
            ? "available"
            : "unavailable";
          setCheckedInput(trimmed);
          setAvailability(nextState);
          onAvailabilityChange?.(nextState, trimmed);
        } catch (err) {
          if (seq !== checkSeqRef.current) return;
          setCheckedInput("");
          setAvailability("error");
          onAvailabilityChange?.("error", "");
          errorToastMessage(err as Error);
        }
      }, 500),
    [onAvailabilityChange],
  );

  useEffect(() => {
    if (readOnly) return;
    const trimmed = sanitizeStudyCodeInput(value);
    if (!trimmed) {
      setAvailability("idle");
      setCheckedInput("");
      onAvailabilityChange?.("idle", "");
      return;
    }
    checkAvailability(value);
  }, [value, checkAvailability, readOnly, onAvailabilityChange]);

  useEffect(() => {
    return () => {
      checkAvailability.cancel();
    };
  }, [checkAvailability]);

  const handleSubmit = async () => {
    if (!studyId || readOnly) return;
    const trimmed = sanitizeStudyCodeInput(value);
    if (
      !trimmed ||
      availability !== "available" ||
      checkedInput !== trimmed
    ) {
      return;
    }

    try {
      setSubmitLoader(true);
      const res: AxiosResponse = await http.post(
        `/study/${studyId}/study-code`,
        { studyCode: trimmed },
      );
      toastMessage("success", res?.data?.message);
      onSuccess?.();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSubmitLoader(false);
    }
  };

  if (readOnly) {
    return (
      <FormControl sx={InputWrapper}>
        <FormLabel sx={LabelStyle}>
          {t("ADD_STUDY_DRAWER.STUDY_CODE_LABEL")}
        </FormLabel>
        <Typography variant="subtitle1" color="text.primary">
          {value || "-"}
        </Typography>
      </FormControl>
    );
  }

  const helperText = (() => {
    if (!sanitizeStudyCodeInput(value)) return " ";
    if (availability === "checking") {
      return t("ADD_STUDY_DRAWER.STUDY_CODE_CHECKING_HINT");
    }
    if (availability === "available") {
      return t("ADD_STUDY_DRAWER.STUDY_CODE_AVAILABLE_HINT");
    }
    if (availability === "unavailable") {
      return t("ADD_STUDY_DRAWER.STUDY_CODE_UNAVAILABLE_HINT");
    }
    if (availability === "error") {
      return t("ADD_STUDY_DRAWER.STUDY_CODE_CHECK_ERROR_HINT");
    }
    return " ";
  })();

  const helperTextColor = (() => {
    if (availability === "available") return "success.main";
    if (availability === "unavailable") return "error.main";
    if (availability === "error") return "warning.main";
    return "text.secondary";
  })();

  const inputEndAdornment = (() => {
    if (availability === "checking") {
      return (
        <InputAdornment position="end">
          <CircularProgress size={18} thickness={5} />
        </InputAdornment>
      );
    }
    if (availability === "available") {
      return (
        <InputAdornment position="end">
          <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "success.main" }} />
        </InputAdornment>
      );
    }
    if (availability === "unavailable") {
      return (
        <InputAdornment position="end">
          <ErrorOutlineRoundedIcon sx={{ fontSize: 20, color: "error.main" }} />
        </InputAdornment>
      );
    }
    if (availability === "error") {
      return (
        <InputAdornment position="end">
          <ErrorOutlineRoundedIcon sx={{ fontSize: 20, color: "warning.main" }} />
        </InputAdornment>
      );
    }
    return undefined;
  })();

  const canSubmit =
    sanitizeStudyCodeInput(value).length > 0 &&
    availability === "available" &&
    checkedInput === sanitizeStudyCodeInput(value);

  return (
    <Box>
      {mode === "settings" && !hideDescription ? (
        <Typography variant="subtitle1" color="text.secondary" mb={2}>
          {t("ADD_STUDY_DRAWER.STUDY_CODE_DESCRIPTION")}
        </Typography>
      ) : null}
      <FormControl sx={InputWrapper}>
        <FormLabel sx={LabelStyle} htmlFor="study-code">
          {t("ADD_STUDY_DRAWER.STUDY_CODE_LABEL")}{" "}
          <span style={{ color: "red" }}>*</span>
        </FormLabel>
        <TextField
          id="study-code"
          placeholder={t("ADD_STUDY_DRAWER.STUDY_CODE_PLACEHOLDER")}
          value={value}
          onChange={(e) => onChange(sanitizeStudyCodeInput(e.target.value))}
          error={availability === "unavailable" || availability === "error"}
          helperText={helperText}
          FormHelperTextProps={{
            sx: { color: helperTextColor },
          }}
          InputProps={{
            endAdornment: inputEndAdornment,
          }}
        />
      </FormControl>

      {mode === "settings" ? (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
          {!submitLoader ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {t("ADD_STUDY_DRAWER.STUDY_CODE_SAVE")}
            </Button>
          ) : (
            <CircularProgress size={25} />
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default StudyCodeRegistration;
